import type { GuestChatDetail, GuestDesk, GuestMessage } from '@deskworks/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { ArrowUp, Briefcase, LayoutDashboard, Loader2, LogOut, MessagesSquare, Plus, UserPlus } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Field } from '@/components/field';
import { LanguageSwitch } from '@/components/language-switch';
import { Logo } from '@/components/logo';
import { Markdown } from '@/components/markdown';
import { EmptyState, ErrorState, LoadingPage, LoadingRows } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ViewHost, type ViewSource } from '@/components/views/view-host';
import { ApiError, errorMessage, setToken } from '@/lib/api';
import { useBrandLook } from '@/lib/brand';
import { formatAbsolute } from '@/lib/format';
import { guestClient, setGuestToken, type GuestClient } from '@/lib/guest';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { useToast } from '@/lib/toast';
import { cn } from '@/lib/utils';

const WORK_STATUS: Record<string, string> = {
  needs_approval: t('waits for the team'),
  created: t('noted'),
  ready: t('about to start'),
  working: t('in progress'),
  waiting: t('waiting'),
  blocked: t('blocked'),
  complete: t('done'),
  failed: t('failed'),
  cancelled: t('cancelled'),
};

/**
 * `/guest/<desk>`: a desk shared by link as its guest sees it (docs/features/desk-sharing.md) — in the account's look,
 * its views, and with write access their own chats with its Lead. Nothing else of the account.
 */
export function GuestDeskPage() {
  const { deskId } = useParams({ from: '/guest/$deskId' });
  const [refused, setRefused] = useState(false);
  const client = useMemo(() => guestClient(deskId, () => setRefused(true)), [deskId]);
  const desk = useQuery({ queryKey: ['guest', deskId], queryFn: () => client.guest.desk(deskId), retry: false, enabled: !refused });
  useBrandLook(desk.data?.branding);
  const [tab, setTab] = useState<string | null>(null);

  if (refused || (desk.isError && desk.error instanceof ApiError && desk.error.status === 401)) {
    return (
      <div className="mx-auto flex min-h-full max-w-md flex-col justify-center px-6 py-12">
        <EmptyState
          title={t('No way in on this device')}
          description={t('Open the link you were sent again. If you registered, sign in first.')}
          action={
            <Button asChild variant="outline">
              <a href={`/login?redirect=${encodeURIComponent(`/guest/${deskId}`)}`}>{t('Sign in')}</a>
            </Button>
          }
        />
      </div>
    );
  }
  if (desk.isPending) return <LoadingPage />;
  if (desk.isError) return <ErrorState error={desk.error} onRetry={() => void desk.refetch()} />;

  const d = desk.data;
  const canChat = d.guest.access === 'write' && !!d.leadName;
  const tabs = [...(canChat ? [{ id: 'chat', label: t('Chat with {name}', { name: d.leadName! }), icon: MessagesSquare }] : []), ...d.views.map((v) => ({ id: v.id, label: v.title, icon: LayoutDashboard }))];
  const current = tab && tabs.some((x) => x.id === tab) ? tab : tabs[0]?.id;

  return (
    <div className="flex min-h-full flex-col bg-background">
      <GuestHeader desk={d} deskId={deskId} client={client} />
      {tabs.length > 1 && (
        <nav className="border-b bg-background/95 px-4 sm:px-8" aria-label={t('Desk')}>
          <div className="mx-auto flex max-w-5xl gap-1 overflow-x-auto">
            {tabs.map((x) => (
              <button
                key={x.id}
                type="button"
                onClick={() => setTab(x.id)}
                className={cn(
                  '-mb-px flex shrink-0 cursor-pointer items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm whitespace-nowrap transition-colors',
                  x.id === current ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                <x.icon className="size-4" /> {x.label}
              </button>
            ))}
          </div>
        </nav>
      )}
      <main className="mx-auto flex w-full max-w-5xl flex-1 flex-col px-4 py-6 sm:px-8">
        {!current ? (
          <EmptyState title={t('Nothing here yet')} description={t('{account} hasn’t put anything on this desk for you yet.', { account: d.accountName })} />
        ) : current === 'chat' ? (
          <GuestChats deskId={deskId} desk={d} client={client} />
        ) : (
          <GuestViewPanel key={current} deskId={deskId} viewId={current} client={client} />
        )}
      </main>
    </div>
  );
}

function GuestHeader({ desk, deskId, client }: { desk: GuestDesk; deskId: string; client: GuestClient }) {
  const navigate = useNavigate();
  const [registering, setRegistering] = useState(false);
  const leave = () => {
    setGuestToken(deskId, null);
    void navigate({ to: '/' });
  };
  return (
    <header className="border-b bg-background px-4 pt-[env(safe-area-inset-top)] sm:px-8">
      <div className="mx-auto flex max-w-5xl items-center gap-3 py-3">
        <Logo src={desk.branding?.logoUrl} className="size-9" />
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-base font-semibold">{desk.deskName}</h1>
          <p className="truncate text-xs text-muted-foreground">{desk.accountName}</p>
        </div>
        <span className="hidden max-w-40 truncate text-sm text-muted-foreground sm:inline" title={desk.guest.name}>
          {desk.guest.name}
        </span>
        {desk.guest.canRegister && (
          <Button size="sm" variant="outline" onClick={() => setRegistering(true)} title={t('Keep your chats and come back from any device.')}>
            <UserPlus /> <span className="max-sm:hidden">{t('Create account')}</span>
          </Button>
        )}
        {!desk.guest.registered && (
          <Button size="icon-sm" variant="ghost" onClick={leave} aria-label={t('Leave on this device')} title={t('Leave on this device')}>
            <LogOut />
          </Button>
        )}
        <LanguageSwitch />
      </div>
      {registering && <RegisterDialog deskId={deskId} client={client} onDone={() => setRegistering(false)} />}
    </header>
  );
}

/** A guest who came by the form registers: same chats, and from now on they sign in with email and password. */
function RegisterDialog({ deskId, client, onDone }: { deskId: string; client: GuestClient; onDone: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const register = useMutation({
    mutationFn: () => client.guest.register(deskId, { email: email.trim(), password }),
    onSuccess: (session) => {
      setToken(session.token);
      setGuestToken(deskId, null);
      void queryClient.invalidateQueries({ queryKey: ['guest', deskId] });
      void queryClient.invalidateQueries({ queryKey: qk.me });
      toast.success(t('Your account is ready. Sign in with {email} from now on.', { email: session.user.email }));
      onDone();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (password.length >= 12) register.mutate();
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onDone()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="contents">
          <DialogHeader>
            <DialogTitle>{t('Create account')}</DialogTitle>
            <DialogDescription>{t('With an account you come back from any device and keep your chats.')}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <Field label={t('Email')}>
              <Input type="email" autoComplete="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <Field label={t('Password')} hint={t('At least 12 characters.')}>
              <Input type="password" autoComplete="new-password" required minLength={12} value={password} onChange={(e) => setPassword(e.target.value)} />
            </Field>
            {register.isError && (
              <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {errorMessage(register.error)}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onDone}>
              {t('Cancel')}
            </Button>
            <Button type="submit" loading={register.isPending}>
              {t('Create account')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function GuestViewPanel({ deskId, viewId, client }: { deskId: string; viewId: string; client: GuestClient }) {
  const view = useQuery({ queryKey: ['guest', deskId, 'view', viewId], queryFn: () => client.guest.view(deskId, viewId) });
  const source = useMemo<ViewSource>(
    () => ({
      data: (id, params) => client.guest.viewData(deskId, id, params),
      query: (id, name, params) => client.guest.viewQuery(deskId, id, name, params),
    }),
    [client, deskId],
  );
  if (view.isPending) return <LoadingRows rows={4} />;
  if (view.isError) return <ErrorState error={view.error} onRetry={() => void view.refetch()} />;
  return (
    <div>
      {view.data.description && <p className="mb-4 text-sm text-muted-foreground">{view.data.description}</p>}
      <ViewHost workspaceId="" viewId={view.data.id} code={view.data.code} version={view.data.version} tables={view.data.tables} source={source} />
    </div>
  );
}

/** The guest's chats with the Lead: the open one, the others to switch to, and a new one. */
function GuestChats({ deskId, desk, client }: { deskId: string; desk: GuestDesk; client: GuestClient }) {
  const chats = useQuery({ queryKey: ['guest', deskId, 'chats'], queryFn: () => client.guest.chats(deskId) });
  const [open, setOpen] = useState<string | null | 'new'>(null);
  const current = open === 'new' ? null : (open ?? chats.data?.[0]?.id ?? null);
  if (chats.isPending) return <LoadingRows rows={3} />;
  if (chats.isError) return <ErrorState error={chats.error} onRetry={() => void chats.refetch()} />;
  return (
    <div className="flex flex-1 flex-col gap-3">
      {!!chats.data.length && (
        <div className="flex flex-wrap items-center gap-1.5">
          {chats.data.slice(0, 6).map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setOpen(c.id)}
              className={cn(
                'max-w-56 cursor-pointer truncate rounded-full border px-3 py-1 text-xs transition-colors',
                c.id === current ? 'border-primary bg-primary/5 text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
              title={c.title}
            >
              {c.title}
            </button>
          ))}
          <Button size="xs" variant="ghost" onClick={() => setOpen('new')} disabled={!current}>
            <Plus /> {t('New chat')}
          </Button>
        </div>
      )}
      <GuestThread key={current ?? 'new'} deskId={deskId} desk={desk} client={client} chatId={current} onStarted={(id) => setOpen(id)} />
    </div>
  );
}

function GuestThread({
  deskId,
  desk,
  client,
  chatId,
  onStarted,
}: {
  deskId: string;
  desk: GuestDesk;
  client: GuestClient;
  chatId: string | null;
  onStarted: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [text, setText] = useState('');
  const bottom = useRef<HTMLDivElement>(null);
  const key = ['guest', deskId, 'chat', chatId] as const;
  // While the Lead writes, look every two seconds; otherwise every fifteen (the team may answer too).
  const chat = useQuery({
    queryKey: key,
    queryFn: () => client.guest.chat(deskId, chatId!),
    enabled: !!chatId,
    refetchInterval: (q) => ((q.state.data as GuestChatDetail | undefined)?.working ? 2000 : 15_000),
  });
  const send = useMutation({
    mutationFn: async (content: string) => {
      if (!chatId) return { started: await client.guest.startChat(deskId, { message: content }) };
      await client.guest.send(deskId, chatId, { content });
      return { started: null };
    },
    onSuccess: ({ started }) => {
      setText('');
      if (started) {
        queryClient.setQueryData(['guest', deskId, 'chat', started.id], started);
        void queryClient.invalidateQueries({ queryKey: ['guest', deskId, 'chats'] });
        onStarted(started.id);
      } else void queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (err) => toast.error(t('Your message was not sent'), errorMessage(err)),
  });

  const messages = chat.data?.messages ?? [];
  const working = chat.data?.working || send.isPending;
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, working]);

  const submit = (content = text) => {
    const value = content.trim();
    if (value && !send.isPending) send.mutate(value);
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };
  const lastAgent = [...messages].reverse().find((m) => m.senderType !== 'system');
  const suggestions = !working && lastAgent?.senderType === 'agent' ? (lastAgent.suggestions ?? []) : [];

  return (
    <Card className="flex min-h-[60vh] flex-1 flex-col overflow-hidden p-0">
      <div className="flex-1 space-y-4 overflow-y-auto p-4 sm:p-6" aria-live="polite">
        {chatId && chat.isPending ? (
          <LoadingRows rows={3} />
        ) : !messages.length ? (
          <EmptyState
            compact
            icon={<MessagesSquare />}
            title={t('Write to {name}', { name: desk.leadName ?? '' })}
            description={desk.deskDescription ?? t('Ask what you need; {name} answers here and takes on work for you.', { name: desk.leadName ?? '' })}
          />
        ) : (
          messages.map((m) => <GuestBubble key={m.id} message={m} me={desk.guest.name} />)
        )}
        {working ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" /> {t('{name} is writing …', { name: desk.leadName ?? '' })}
          </div>
        ) : (
          chat.data?.failed && (
            <p className="text-xs text-muted-foreground">{t('{name} can’t answer right now. The team sees your message.', { name: desk.leadName ?? '' })}</p>
          )
        )}
        <div ref={bottom} />
      </div>
      <div className="border-t bg-muted/30 p-3">
        {!!suggestions.length && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {suggestions.map((s) => (
              <Button key={s} size="xs" variant="outline" onClick={() => submit(s)}>
                {s}
              </Button>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKey}
            rows={2}
            maxLength={20_000}
            placeholder={t('Your message …')}
            className="min-h-11 flex-1 resize-none bg-background"
            aria-label={t('Your message')}
          />
          <Button size="icon" onClick={() => submit()} disabled={!text.trim()} loading={send.isPending} aria-label={t('Send')}>
            <ArrowUp />
          </Button>
        </div>
      </div>
    </Card>
  );
}

function GuestBubble({ message, me }: { message: GuestMessage; me: string }) {
  if (message.senderType === 'system') {
    return (
      <div className="flex justify-center">
        <div className="flex max-w-[90%] items-center gap-1.5 rounded-lg bg-muted px-3 py-1.5 text-xs text-muted-foreground">
          <Briefcase className="size-3.5 shrink-0" />
          <span className="font-medium text-foreground/80">{message.workItem?.title}</span>
          {message.workItem?.status && <span>· {WORK_STATUS[message.workItem.status] ?? message.workItem.status}</span>}
        </div>
      </div>
    );
  }
  const mine = message.senderType === 'user' && message.senderName === me;
  return (
    <div className={cn('flex flex-col gap-1', mine ? 'items-end' : 'items-start')}>
      <span className="px-1 text-xs text-muted-foreground" title={formatAbsolute(message.createdAt, true)}>
        {mine ? t('You') : message.senderName}
      </span>
      <div className={cn('max-w-[85%] rounded-2xl px-4 py-2.5', mine ? 'bg-primary text-primary-foreground' : 'bg-muted')}>
        {mine ? <p className="text-sm whitespace-pre-wrap">{message.content}</p> : <Markdown text={message.content} />}
      </div>
    </div>
  );
}

